import {
  CONNECTOR_BODY_METHODS,
  CONNECTOR_FORBIDDEN_HEADER_PREFIX,
  CONNECTOR_FORBIDDEN_HEADERS,
  CONNECTOR_HEADER_NAME_PATTERN,
  CONNECTOR_LIMITS,
  CONNECTOR_MAP_TO_PATTERN,
  CONNECTOR_QUERY_KEY_PATTERN,
  CONNECTOR_VARIABLE_PREFIX,
  isConnectorErrorType,
} from "@sagot/constants";
import type { HttpRequestMapping, HttpRequestStep } from "../types";
import type { ConnectorTestIn } from "./connector-api-types";

/**
 * Pure helpers of the Data connector form: rows, the checks the server repeats, the variables a request uses and the
 * body of "Test request". The server judges the saved step (`graph_p5.py`); these only let the form say so first.
 */

/** A `{{variable}}` of the server's custom-body check: swapped for a plain value before the body is read as JSON. */
const PLACEHOLDER = /\{\{\s*[a-z_][a-z0-9_.]*\s*\}\}/g;
/** A `{{name}}` or `{{name | fallback}}`: the name is captured. */
const PLACEHOLDER_NAME = /\{\{\s*([a-z_][a-z0-9_.]*)\s*(?:\|[^{}]*)?\}\}/g;
const PATH_FORBIDDEN_CHARS = /[?#\\@\s]/;
const CONTROL_BELOW = 0x20;
const ENCODED_SEPARATOR = /%(?:2f|5c)/i;
const SAMPLE_KEY = /^[a-z][a-z0-9_.]{0,59}$/;
const POINTER_ESCAPE = /~(?![01])/;
const VARIABLE_CHARS = /[^a-z0-9]+/g;
const EDGE_UNDERSCORES = /^_+|_+$/g;
const FALLBACK_VARIABLE = "value";

/** Shown as code, never translated: examples that carry `{{variables}}` (a translation would try to fill them in). */
export const CONNECTOR_EXAMPLES = {
  path: "/students/{{var.student_no}}",
  variable: "{{var.student_no}}",
  quotedVariable: '"{{var.student_no}}"',
  body: '{\n  "student": "{{var.student_no}}"\n}',
  mapped: "{{var.name}}",
} as const;

/** What the steps after a request can read about it: `step.<id>.ok` (true / false) and `step.<id>.status` (the HTTP status, 0 when nothing answered), keyed by the step's id. */
export const factOf = (step: Pick<HttpRequestStep, "id">, fact: "ok" | "status"): string => `{{step.${step.id}.${fact}}}`;

// ------------------------------------------------------------------ method and body

export const bodyAllowed = (method: HttpRequestStep["method"]): boolean => CONNECTOR_BODY_METHODS.has(method);

export type BodyProblem = "json" | "object";

/** Why a body template is not usable: not JSON once its variables are plain values, or not an object. Nothing for no body. */
export function bodyProblem(body: string | undefined): BodyProblem | null {
  if (body === undefined || body.trim() === "") return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(body.replace(PLACEHOLDER, "x"));
  } catch {
    return "json";
  }
  return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed) ? null : "object";
}

// ------------------------------------------------------------------ path

export type PathProblem = "long" | "slash" | "chars" | "dots";

const hasControlCharacter = (text: string): boolean => Array.from(text).some((char) => char.charCodeAt(0) < CONTROL_BELOW);

const decodeOnce = (text: string): string => {
  try {
    return decodeURIComponent(text);
  } catch {
    return text;
  }
};

/** A segment that is `.` or `..` once percent-decoding is done twice (`%2e%2e`, `%252e%252e`). */
const isDotSegment = (segment: string): boolean => {
  const decoded = decodeOnce(decodeOnce(segment));
  return decoded === "." || decoded === "..";
};

/** The path is appended to the endpoint's address: a slash first, no query, fragment, `@`, backslash or `..` segment. */
export function pathProblem(path: string | undefined): PathProblem | null {
  if (!path) return null;
  if (path.length > CONNECTOR_LIMITS.path) return "long";
  if (!path.startsWith("/")) return "slash";
  if (PATH_FORBIDDEN_CHARS.test(path) || ENCODED_SEPARATOR.test(path) || hasControlCharacter(path)) return "chars";
  return path.replace(PLACEHOLDER, "x").split("/").some(isDotSegment) ? "dots" : null;
}

// ------------------------------------------------------------------ query and header rows

/** A query parameter or a header while it is edited: a row may be half written, the step only keeps the finished ones. */
export interface Row {
  key: string;
  value: string;
}

export const rowsOf = (record: Readonly<Record<string, string>> | undefined): Row[] => Object.entries(record ?? {}).map(([key, value]) => ({ key, value }));

/** The rows that have a name, as the step stores them (a name used twice keeps its last value); `undefined` when none. */
export function recordOf(rows: readonly Row[]): Record<string, string> | undefined {
  const named = rows.filter((row) => row.key.trim() !== "");
  return named.length === 0 ? undefined : Object.fromEntries(named.map((row) => [row.key.trim(), row.value]));
}

export type RowKind = "query" | "header";
export type RowProblem = "name" | "forbidden" | "duplicate" | "value";

export const isForbiddenHeader = (name: string): boolean => {
  const lower = name.trim().toLowerCase();
  return CONNECTOR_FORBIDDEN_HEADERS.has(lower) || lower.startsWith(CONNECTOR_FORBIDDEN_HEADER_PREFIX);
};

/** What is wrong with each row (`null`: nothing). A blank row is not wrong, it is just not kept. */
export function rowProblems(rows: readonly Row[], kind: RowKind): (RowProblem | null)[] {
  const header = kind === "header";
  const pattern = header ? CONNECTOR_HEADER_NAME_PATTERN : CONNECTOR_QUERY_KEY_PATTERN;
  const valueMax = header ? CONNECTOR_LIMITS.headerValue : CONNECTOR_LIMITS.queryValue;
  const seen = new Set<string>();
  return rows.map((row) => {
    const name = row.key.trim();
    if (name === "") return null;
    if (!pattern.test(name)) return "name";
    if (header && isForbiddenHeader(name)) return "forbidden";
    const identity = header ? name.toLowerCase() : name;
    if (seen.has(identity)) return "duplicate";
    seen.add(identity);
    return row.value.length > valueMax ? "value" : null;
  });
}

// ------------------------------------------------------------------ response mapping

export type PointerProblem = "empty" | "slash" | "escape" | "long";

/** A JSON Pointer (RFC 6901) into the response; the whole document (an empty pointer) is not a value. */
export function pointerProblem(pointer: string): PointerProblem | null {
  if (pointer === "") return "empty";
  if (pointer.length > CONNECTOR_LIMITS.pointer) return "long";
  if (!pointer.startsWith("/")) return "slash";
  return POINTER_ESCAPE.test(pointer) ? "escape" : null;
}

export type MappingProblem = "pointer" | "variable" | "duplicate";

/** Per mapping row: a pointer that is not one, a target that is not `var.<name>`, or a target used by an earlier row. */
export function mappingProblems(map: readonly HttpRequestMapping[]): (MappingProblem | null)[] {
  const targets = new Set<string>();
  return map.map((row) => {
    if (pointerProblem(row.from) !== null) return "pointer";
    if (!CONNECTOR_MAP_TO_PATTERN.test(row.to)) return "variable";
    if (targets.has(row.to)) return "duplicate";
    targets.add(row.to);
    return null;
  });
}

export const variableNameOf = (target: string): string => (target.startsWith(CONNECTOR_VARIABLE_PREFIX) ? target.slice(CONNECTOR_VARIABLE_PREFIX.length) : target);
export const variableTarget = (name: string): string => `${CONNECTOR_VARIABLE_PREFIX}${name}`;

/** A variable name for a response field: its last segment in lower case (`/data/Grade-Level` gives `var.grade_level`), not used yet. */
export function suggestVariable(pointer: string, taken: ReadonlySet<string>): string {
  const last = pointer.split("/").at(-1) ?? "";
  const unescaped = last.replace(/~1/g, "/").replace(/~0/g, "~");
  const base = unescaped.toLowerCase().replace(VARIABLE_CHARS, "_").replace(EDGE_UNDERSCORES, "").slice(0, CONNECTOR_LIMITS.variableName - 3) || FALLBACK_VARIABLE;
  let target = variableTarget(base);
  for (let n = 2; taken.has(target); n += 1) target = variableTarget(`${base}_${String(n)}`);
  return target;
}

/** The step with a mapping for `pointer` (a new variable named after the field); unchanged when it is mapped already or all rows are used. */
export function withMappingFor(step: HttpRequestStep, pointer: string): HttpRequestStep {
  const map = step.map ?? [];
  if (map.length >= CONNECTOR_LIMITS.mapRows || map.some((row) => row.from === pointer)) return step;
  return { ...step, map: [...map, { from: pointer, to: suggestVariable(pointer, new Set(map.map((row) => row.to))) }] };
}

/** The variables the step fills, in order: what the steps after it can use as `{{var.name}}` (plus `step.<name>.ok` and `.status`). */
export const mappedVariables = (step: HttpRequestStep): string[] => (step.map ?? []).filter((row) => CONNECTOR_MAP_TO_PATTERN.test(row.to)).map((row) => row.to);

// ------------------------------------------------------------------ timeout

export const timeoutCapOf = (facing: boolean): number => (facing ? CONNECTOR_LIMITS.facingTimeoutMsMax : CONNECTOR_LIMITS.timeoutMsMax);

/** Whole seconds shown in the form, kept inside what the server takes. */
export const secondsOf = (timeoutMs: number): number => Math.round(timeoutMs / 1000);
export const timeoutFromSeconds = (seconds: number): number => Math.min(Math.max(seconds * 1000, CONNECTOR_LIMITS.timeoutMsMin), CONNECTOR_LIMITS.timeoutMsMax);

// ------------------------------------------------------------------ variables of the request and the sample

/** Every `{{variable}}` the request uses (path, query values, header values, body), once each, in the order they appear. */
export function placeholdersOf(step: HttpRequestStep): string[] {
  const texts = [step.path ?? "", ...Object.values(step.query ?? {}), ...Object.values(step.headers ?? {}), bodyAllowed(step.method) ? (step.body ?? "") : ""];
  const names = new Set<string>();
  for (const text of texts) for (const match of text.matchAll(PLACEHOLDER_NAME)) if (match[1]) names.add(match[1]);
  return [...names].filter((name) => SAMPLE_KEY.test(name)).slice(0, CONNECTOR_LIMITS.sampleRows);
}

/** The sample values for the variables the request uses now: what was typed is kept, a new variable starts empty. */
export function sampleFor(step: HttpRequestStep, typed: Readonly<Record<string, string>>): Record<string, string> {
  return Object.fromEntries(placeholdersOf(step).map((name) => [name, typed[name] ?? ""]));
}

/** What "Test request" sends: the step as it is now (a body only where the method has one, finished rows only) and the sample values that were filled in. */
export function testRequestOf(step: HttpRequestStep, sample: Readonly<Record<string, string>>): ConnectorTestIn {
  const request: ConnectorTestIn = {
    endpoint_id: step.endpoint_id,
    method: step.method,
    timeout_ms: step.timeout_ms,
    map: (step.map ?? []).filter((row) => row.from !== "" && row.to !== ""),
    sample: Object.fromEntries(Object.entries(sample).filter(([, value]) => value !== "")),
  };
  if (step.path) request.path = step.path;
  if (step.query && Object.keys(step.query).length > 0) request.query = step.query;
  if (step.headers && Object.keys(step.headers).length > 0) request.headers = step.headers;
  if (bodyAllowed(step.method) && step.body) request.body = step.body;
  return request;
}

// ------------------------------------------------------------------ the step as a whole

/** Whether "Test request" can be sent: an endpoint is chosen and nothing the form checks is wrong. */
export function requestProblem(step: HttpRequestStep): string | null {
  if (!step.endpoint_id) return "endpoint";
  if (pathProblem(step.path)) return "path";
  if (bodyAllowed(step.method) && bodyProblem(step.body)) return "body";
  if (rowProblems(rowsOf(step.query), "query").some(Boolean) || rowProblems(rowsOf(step.headers), "header").some(Boolean)) return "rows";
  return null;
}

/** The copy key of a failure reason, `unknown` for one this build does not know (the raw text is never shown). */
export const errorKeyOf = (errorType: string | null): string => (errorType !== null && isConnectorErrorType(errorType) ? errorType : "unknown");
